import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Mail, Phone, MapPin, Package, Building2, Trash2 } from "lucide-react";
import { Logo, Wordmark } from "./Brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "av_admin_token";

const STATUSES = ["new", "contacted", "closed"];
const STATUS_STYLE = {
  new: "bg-[#B08D57] text-white",
  contacted: "bg-[#2d4a3e] text-white",
  closed: "bg-[#333] text-white/70",
};

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Welcome back.");
      onLogin(data.access_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111] grain flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-[400px]">
        <div className="flex flex-col items-center mb-10">
          <Logo className="w-16 h-16" rounded="rounded-2xl" glow />
          <div className="mt-5">
            <Wordmark color="#F8F7F3" spacing="0.3em" />
          </div>
          <p className="overline text-white/40 mt-4">Buyer Inbox · Admin</p>
        </div>
        <div className="space-y-6">
          <div>
            <label className="overline text-white/50 text-[11px] block mb-3">Email</label>
            <input
              data-testid="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-white/20 py-2 text-[#F8F7F3] outline-none focus:border-[#B08D57] transition-colors"
            />
          </div>
          <div>
            <label className="overline text-white/50 text-[11px] block mb-3">Password</label>
            <input
              data-testid="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-white/20 py-2 text-[#F8F7F3] outline-none focus:border-[#B08D57] transition-colors"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-btn"
            className="w-full rounded-full bg-[#F8F7F3] text-[#111] font-head font-semibold py-3.5 hover:bg-[#B08D57] hover:text-white transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </div>
      </form>
    </div>
  );
}

function InboxView({ token, onLogout }) {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/quotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotes(data);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(
        `${API}/admin/quotes/${id}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQuotes((qs) => qs.map((q) => (q.id === id ? { ...q, status } : q)));
      toast.success(`Marked ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const removeQuote = async (id) => {
    if (!window.confirm("Delete this quote request permanently?")) return;
    try {
      await axios.delete(`${API}/admin/quotes/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotes((qs) => qs.filter((q) => q.id !== id));
      toast.success("Quote deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  const counts = STATUSES.reduce((a, s) => ({ ...a, [s]: quotes.filter((q) => q.status === s).length }), {});

  return (
    <div className="min-h-screen bg-[#F8F7F3]">
      <header className="bg-[#111] text-white">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="w-10 h-10" rounded="rounded-lg" />
            <div>
              <Wordmark color="#F8F7F3" size="text-sm" />
              <p className="overline text-white/40 text-[10px] mt-1">Buyer Inbox</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="refresh-btn"
              className="rounded-full border border-white/20 px-4 py-2 text-sm flex items-center gap-2 hover:bg-white/10 transition-colors"
            >
              <RefreshCw size={15} /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="rounded-full bg-[#B08D57] px-4 py-2 text-sm flex items-center gap-2 hover:bg-[#987748] transition-colors"
            >
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-3 gap-4 mb-10 max-w-[520px]">
          {STATUSES.map((s) => (
            <div key={s} className="bg-white border border-[#E7E1D6] p-5">
              <p className="font-display text-3xl text-[#111]">{counts[s] || 0}</p>
              <p className="overline text-[#666] mt-1 capitalize">{s}</p>
            </div>
          ))}
        </div>

        <h1 className="font-display text-3xl text-[#111] mb-6">Quote Requests</h1>

        {loading ? (
          <p className="font-body text-[#666]">Loading…</p>
        ) : quotes.length === 0 ? (
          <p className="font-body text-[#666]" data-testid="empty-state">No quote requests yet.</p>
        ) : (
          <div className="space-y-4" data-testid="quotes-list">
            {quotes.map((q) => (
              <div key={q.id} className="bg-white border border-[#E7E1D6] p-6 md:p-7" data-testid="quote-row">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-head font-bold text-lg text-[#111]">{q.name}</h3>
                      <span className={`text-[11px] font-head font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[q.status] || STATUS_STYLE.new}`}>
                        {q.status}
                      </span>
                    </div>
                    <p className="font-body text-sm text-[#666]">
                      {new Date(q.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        onClick={() => setStatus(q.id, s)}
                        disabled={q.status === s}
                        className={`text-xs font-head px-3 py-1.5 rounded-full border transition-colors ${
                          q.status === s
                            ? "border-[#111] bg-[#111] text-white"
                            : "border-[#E7E1D6] text-[#666] hover:border-[#B08D57] hover:text-[#B08D57]"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                    <button
                      onClick={() => removeQuote(q.id)}
                      data-testid="delete-quote-btn"
                      title="Delete"
                      className="ml-1 p-2 rounded-full border border-[#E7E1D6] text-[#999] hover:border-red-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2 mt-5 text-sm font-body text-[#333]">
                  <span className="flex items-center gap-2"><Mail size={14} className="text-[#B08D57]" /> <a href={`mailto:${q.email}`} className="hover:text-[#B08D57]">{q.email}</a></span>
                  {q.phone && <span className="flex items-center gap-2"><Phone size={14} className="text-[#B08D57]" /> {q.phone}</span>}
                  {q.country && <span className="flex items-center gap-2"><MapPin size={14} className="text-[#B08D57]" /> {q.country}</span>}
                  {q.company && <span className="flex items-center gap-2"><Building2 size={14} className="text-[#B08D57]" /> {q.company}</span>}
                  {q.product_interest && <span className="flex items-center gap-2"><Package size={14} className="text-[#B08D57]" /> {q.product_interest}</span>}
                </div>
                {q.message && (
                  <p className="mt-4 pt-4 border-t border-[#E7E1D6] font-body text-[15px] text-[#444] leading-relaxed">
                    {q.message}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function AdminDashboard() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    toast("Signed out.");
  }, []);

  if (!token) return <LoginView onLogin={setToken} />;
  return <InboxView token={token} onLogout={logout} />;
}
